/**
 * assertPublishableMirror.js — Employer v1.1 Phase 0.6 Slice C.
 *
 * Client-side mirror of `backend/core/employer_role.py::assert_publishable`.
 * Returns the ORDERED list of blocking rule violations for the given
 * (title, structured_definition_json). An empty list means the role
 * is client-publishable.
 *
 * The mirror is a CONVENIENCE LAYER ONLY (pre-code plan §5.1). The
 * backend Pydantic + DB CHECK + `assert_publishable` remain the
 * source of truth. The wizard surfaces the backend's `detail` string
 * verbatim on a 400 at publish time (pre-code plan §2.10).
 */

export const BLOCKER_CODES = Object.freeze({
  NO_TITLE:              'NO_TITLE',
  MISSING_DOMAIN:        'MISSING_DOMAIN',
  MISSING_COMP_BAND:     'MISSING_COMP_BAND',
  NO_RESPONSIBILITIES:   'NO_RESPONSIBILITIES',
  NO_MUST_HAVE_SKILLS:   'NO_MUST_HAVE_SKILLS',
});

/**
 * @param {{ title: string|null, definition: object|null }} args
 * @returns {Array<{ code: string, step: 1|2, message: string, fixHint: string }>}
 */
export function assertPublishableMirror({ title, definition }) {
  const out = [];
  const t = (title || '').trim();
  const d = (definition && typeof definition === 'object') ? definition : {};

  // B-5 — title is a convenience pre-check (backend enforces on POST).
  if (!t) {
    out.push({
      code:    BLOCKER_CODES.NO_TITLE,
      step:    1,
      message: 'Add a role title.',
      fixHint: 'A title is required to identify the role.',
    });
  }

  // B-1 — must_have_domain
  if (!d.must_have_domain || String(d.must_have_domain).trim() === '') {
    out.push({
      code:    BLOCKER_CODES.MISSING_DOMAIN,
      step:    1,
      message: 'Choose a primary domain.',
      fixHint: 'Drives the must-have filter at evaluation time.',
    });
  }

  // B-2 — comp_band (currency + min required)
  const comp = d.comp_band;
  const compOk =
    comp &&
    typeof comp === 'object' &&
    typeof comp.currency === 'string' &&
    comp.currency.trim() !== '' &&
    comp.min !== null &&
    comp.min !== undefined &&
    Number.isFinite(Number(comp.min));
  if (!compOk) {
    out.push({
      code:    BLOCKER_CODES.MISSING_COMP_BAND,
      step:    1,
      message: 'Specify a compensation band (currency + minimum required).',
      fixHint: 'Min is required; Max is optional but recommended.',
    });
  }

  // B-3 — at least one responsibility
  const responsibs = Array.isArray(d.responsibilities) ? d.responsibilities : [];
  if (responsibs.filter((r) => (r || '').trim() !== '').length < 1) {
    out.push({
      code:    BLOCKER_CODES.NO_RESPONSIBILITIES,
      step:    2,
      message: 'Add at least one responsibility.',
      fixHint: 'Even one bullet makes downstream evaluation possible.',
    });
  }

  // B-4 — at least one must-have skill
  const must = Array.isArray(d.must_have_skills) ? d.must_have_skills : [];
  if (must.filter((s) => (s || '').trim() !== '').length < 1) {
    out.push({
      code:    BLOCKER_CODES.NO_MUST_HAVE_SKILLS,
      step:    2,
      message: 'Add at least one must-have skill.',
      fixHint: 'Must-have skills are the hard gates at evaluation time.',
    });
  }

  return out;
}

export default assertPublishableMirror;
